import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode, lazy, Suspense } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { vendorsApi } from '../api/vendors';
import { setAccessToken, setNativeSessionScope } from '../api/client';
import { SESSION_TRANSITION_EVENT } from '../api/authSession';
import { VendorRealtimeBridge } from '../components/Common/VendorRealtimeBridge';
import { rememberShopSlug, clearRememberedShopSlug, authPath, scopedPath } from '../utils/shopSlug';
import { isPublicAuthPath } from '../utils/publicAuthPath';
import type { AuthUser, Vendor, LoginRequest, RegisterRequest } from '../types';

interface AuthContextType {
  user: AuthUser | null;
  vendor: Vendor | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const PendingPreparationPortal = lazy(() => import('../components/OnboardingPreparation').then(module => ({ default: module.PendingPreparationPortal })));

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const identityGeneration = useRef(0);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const clearIdentity = () => {
      ++identityGeneration.current;
      queryClient.clear();
      setUser(null);
      setVendor(null);
    };
    // SSO exchanges can start outside these context methods in an already
    // mounted SPA. Fence the old profile and remove its cached customer data.
    window.addEventListener(SESSION_TRANSITION_EVENT, clearIdentity);
    return () => window.removeEventListener(SESSION_TRANSITION_EVENT, clearIdentity);
  }, [queryClient]);

  const refreshProfile = useCallback(async () => {
    const generation = identityGeneration.current;
    try {
      const profile = await vendorsApi.getProfile();
      if (generation === identityGeneration.current) setVendor(profile);
    } catch {
      // Profile fetch failed, user may not be fully set up
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const generation = identityGeneration.current;
      queryClient.clear();
      // SSO pages are also public: they must exchange their signed link
      // without a concurrent refresh rotating an older cookie over it.
      if (isPublicAuthPath(window.location.pathname)) {
        setIsLoading(false);
        return;
      }

      try {
        const tokens = await authApi.refreshToken();
        if (tokens.access_token) {
          // Fetch profile once and use it for both user and vendor state
          const profile = await vendorsApi.getProfile();
          if (generation !== identityGeneration.current) return;
          setUser({
            id: profile.id,
            email: profile.email,
            business_name: profile.business_name,
            status: profile.status,
            // Wave-61: backend now surfaces shop_slug on the profile
            // response so the merchant brand (logo + name) survives
            // page refreshes — without this the sidebar fell back to
            // a generic "V / Vendor Portal" header on every reload.
            shop_slug: (profile as any).shop_slug ?? '',
          });
          setVendor(profile);
        }
      } catch {
        if (generation !== identityGeneration.current) return;
        queryClient.clear();
        setAccessToken(null);
        setNativeSessionScope(null);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, [refreshProfile, queryClient]);

  const login = async (data: LoginRequest) => {
    ++identityGeneration.current;
    // Queries and mutations can contain another vendor's customer information.
    // Clear (and cancel) them before publishing the newly authenticated identity.
    queryClient.clear();
    setUser(null);
    setVendor(null);
    const pending = authApi.login(data);
    const generation = identityGeneration.current;
    const result = await pending;
    if (generation !== identityGeneration.current) return;
    // Wave-66: persist the marketplace slug so a future return visit
    // to bare `/login` (no slug in URL) can auto-redirect rather than
    // toast "use the marketplace-specific URL".
    rememberShopSlug(data.shop_slug);
    // Hydrate vendor profile BEFORE flipping user state so consumers of
    // useAuth see a fully populated context on their first render after
    // login (dashboards read vendor.logo_url, vendor.status, etc.).
    try {
      const profile = await vendorsApi.getProfile();
      if (generation === identityGeneration.current) setVendor(profile);
    } catch {
      // Non-fatal; vendor card will lazily hydrate on next refresh.
    }
    if (generation === identityGeneration.current) setUser(result.user);
  };

  const register = async (data: RegisterRequest) => {
    ++identityGeneration.current;
    queryClient.clear();
    setUser(null);
    setVendor(null);
    const pending = authApi.register(data);
    const generation = identityGeneration.current;
    const result = await pending;
    if (generation !== identityGeneration.current) return;
    if (!result.tokens?.access_token) return;
    // Wave-66: same as login — persist for return-visit recovery.
    rememberShopSlug(data.shop_slug);
    try {
      const profile = await vendorsApi.getProfile();
      if (generation === identityGeneration.current) setVendor(profile);
    } catch {
      // Fresh registration may not yet have a vendor row; safe to ignore.
    }
    if (generation === identityGeneration.current) setUser(result.user);
  };

  const logout = async () => {
    ++identityGeneration.current;
    // Remove cached private data immediately, including when the logout request
    // fails or is slow. A later login must fetch with its own credentials.
    queryClient.clear();
    setUser(null);
    setVendor(null);
    const pending = authApi.logout();
    const generation = identityGeneration.current;
    try {
      await pending;
    } finally {
      if (generation === identityGeneration.current) {
        queryClient.clear();
        setUser(null);
        setVendor(null);
        setAccessToken(null);
        setNativeSessionScope(null);
        // Wave-66: explicit logout = user wants a clean slate. Clearing
        // the remembered slug avoids surprising them with a redirect on
        // the next visit (a shared device, a re-routed deep link, etc.).
        clearRememberedShopSlug();
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        vendor,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Wave-67: preserve the marketplace slug so the bounced user
    // lands on `/<slug>/login` not the slug-less `/login`. Without
    // this they'd see the amber "No marketplace selected" banner
    // every time their session expired mid-session — confusing for
    // a vendor who never left their merchant's portal.
    return <Navigate to={authPath('login')} state={{ from: location }} replace />;
  }

  if (user?.status === 'pending') return <Suspense fallback={<p role="status">Loading preparation workspace…</p>}><PendingPreparationPortal /></Suspense>;

  // Vendor status gate. Only `approved` vendors get the full portal.
  // Rejected / suspended / pending see a status screen instead of the
  // dashboard — backend blocks their JWT-issuance path too, so this
  // is defense in depth.
  if (user && user.status && user.status !== 'approved') {
    return <VendorStatusScreen status={user.status} />;
  }

  return (
    <>
      <VendorRealtimeBridge />
      {children}
    </>
  );
}

function VendorStatusScreen({ status }: { status: string }) {
  const copy: Record<string, { title: string; body: string }> = {
    pending: {
      title: 'Your account is pending approval',
      body: 'The marketplace admin is reviewing your application. You\'ll receive an email as soon as it\'s approved. You can close this tab — no action needed.',
    },
    rejected: {
      title: 'Your account was not approved',
      body: 'Please contact the marketplace admin if you believe this is a mistake.',
    },
    suspended: {
      title: 'Your account is suspended',
      body: 'Access to this vendor portal is paused. Please contact the marketplace admin for next steps.',
    },
  };
  const { title, body } = copy[status] ?? {
    title: 'Your account is not active',
    body: 'Please contact the marketplace admin.',
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        <p className="mt-3 text-sm text-gray-600">{body}</p>
      </div>
    </div>
  );
}

export function PublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Wave-69: protected routes are under /:shop_slug/*. Use
      // scopedPath to attach the marketplace context.
      navigate(scopedPath('/dashboard'), { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}
